import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import { IoArrowBack } from "react-icons/io5";

import {
  getNotifications,
  getCustomChat,
  getCustomUser,
  getCustomBanner,
} from "../api";

import "../styles/sifaris.css";

const NotificationScreen = () => {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const groupNotificationsByDate = (items) => {
    if (!items || items.length === 0) return [];

    const sortedNotifications = [...items].sort(
      (a, b) =>
        moment(b.createdAt, ["DD MMMM YYYY, HH:mm", "YYYY-MM-DD HH:mm:ss"]).valueOf() -
        moment(a.createdAt, ["DD MMMM YYYY, HH:mm", "YYYY-MM-DD HH:mm:ss"]).valueOf()
    );

    const grouped = sortedNotifications.reduce((groups, notification) => {
      const notificationDate = moment(
        notification.createdAt,
        ["DD MMMM YYYY, HH:mm", "YYYY-MM-DD HH:mm:ss"]
      )
        .tz("Asia/Baku")
        .format("DD MMMM YYYY");

      if (!groups[notificationDate]) {
        groups[notificationDate] = [];
      }

      groups[notificationDate].push(notification);
      return groups;
    }, {});

    return Object.entries(grouped).map(([date, data]) => ({
      title: date === moment().tz("Asia/Baku").format("DD MMMM YYYY") ? "Bugün" : date,
      data,
      id: `sec-${date}`,
    }));
  };

  const formatPhoneNumberForWhatsApp = (phoneNumber) => {
    const p = String(phoneNumber || "");
    if (p.startsWith("0")) {
      return `994${p.slice(1)}`;
    }
    return p.replace(/\D/g, "");
  };

  useEffect(() => {
    const fetchNotificationsData = async () => {
      try {
        const userId = localStorage.getItem("id");
        const userType = localStorage.getItem("userType");

        if (!userId) {
          console.error("User ID tapılmadı");
          setNotifications([]);
          return;
        }

        const response = await getNotifications();
        if (!response?.data) {
          setNotifications([]);
          return;
        }

        const userNotifications = response.data.filter(
          (notif) => String(notif.userId) === String(userId)
        );

        if (userNotifications.length === 0) {
          setNotifications([]);
          return;
        }

        const filteredNotifications =
          userType !== "customer"
            ? userNotifications.filter((notif) => notif.type === "banner")
            : userNotifications;

        const enhancedNotifications = await Promise.all(
          filteredNotifications.map(async (notif) => {
            let chatTitle = "Söhbət Tapılmadı";
            let courierPhoto = null;
            let courierPhone = "";

            if (
              notif.chatId &&
              typeof notif.chatId === "string" &&
              notif.chatId.trim() !== ""
            ) {
              try {
                if (notif.type === "banner") {
                  const bannerResponse = await getCustomBanner(notif.chatId);
                  chatTitle =
                    bannerResponse?.data?.description || "Reklam Başlığı Tapılmadı";
                } else {
                  const chatResponse = await getCustomChat(notif.chatId);
                  chatTitle = chatResponse?.data?.message || "Söhbət Tapılmadı";
                }
              } catch (error) {
                console.log("chat/banner title error:", error?.message);
              }
            }

            if (notif.courierId) {
              try {
                const courierResponse = await getCustomUser(notif.courierId);
                courierPhoto = courierResponse?.data?.identifyPhoto?.[0] || null;
                courierPhone = courierResponse?.data?.phoneNumber || "";
              } catch (error) {
                console.log("courier info error:", error?.message);
              }
            }

            return {
              ...notif,
              chatTitle,
              courierPhoto,
              courierPhone,
              formattedDate: moment(
                notif.createdAt,
                ["DD MMMM YYYY, HH:mm", "YYYY-MM-DD HH:mm:ss"]
              )
                .tz("Asia/Baku")
                .format("DD MMMM YYYY, HH:mm"),
            };
          })
        );

        setNotifications(groupNotificationsByDate(enhancedNotifications));
      } catch (error) {
        console.error("Bildirişləri yükləmə zamanı xəta:", error);
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationsData();
  }, []);

  const sections = useMemo(() => notifications, [notifications]);

  return (
    <div className="sq-notification-page">

        
      <div className="sq-notification-header">
        <button
          type="button"
          className="sq-notification-back-btn"
          onClick={() => navigate(-1)}
        >
          <IoArrowBack size={22} />
        </button>

        <h1 className="sq-notification-title">Bildirişlər</h1>
      </div>

      {loading ? (
        <div className="sq-notification-loading">Yüklənir...</div>
      ) : sections.length === 0 ? (
        <div className="sq-no-notification">📭 Bildiriş yoxdur.</div>
      ) : (
        <div className="sq-notification-list">
          {sections.map((section) => (
            <div key={section.id}>
              <div className="sq-notification-section-header">
                <span className="sq-notification-section-text">{section.title}</span>
              </div>

              {section.data.map((item) => (
                <div key={item.id} className="sq-notification-card">
                  <div className="sq-notification-card-top">
                    <div className="sq-notification-chat-title">{item.chatTitle}</div>
                    <div className="sq-notification-date">{item.formattedDate}</div>
                  </div>

                  <div className="sq-notification-message">{item.message}</div>

                  <div className="sq-notification-divider" />

                  {(item.courierPhoto || item.courierPhone) && (
                    <div className="sq-notification-courier-row">
                      {item.courierPhoto ? (
                        <button
                          type="button"
                          className="sq-notification-image-btn"
                          onClick={() => setSelectedImage(item.courierPhoto)}
                        >
                          <img
                            src={item.courierPhoto}
                            alt="Kuryer"
                            className="sq-notification-courier-image"
                          />
                        </button>
                      ) : (
                        <div className="sq-notification-courier-placeholder" />
                      )}

                      <div className="sq-notification-courier-info">
                        <div className="sq-notification-courier-label">Kuryer</div>
                        <div className="sq-notification-courier-phone">
                          {item.courierPhone || "—"}
                        </div>
                      </div>

                      {item.courierPhone && (
                        <a
                          className="sq-notification-whatsapp-btn"
                          href={`https://wa.me/${formatPhoneNumberForWhatsApp(
                            item.courierPhone
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          WhatsApp
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {selectedImage && (
        <div
          className="sq-preview-backdrop"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="sq-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage}
              alt="Preview"
              className="sq-preview-image"
            />
            <button
              type="button"
              className="sq-preview-close"
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationScreen;